import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShellLayout } from './components/ShellLayout'
import { RequireAuth } from './components/RequireAuth'
import { SettingsModal } from './components/SettingsModal'
import { WelcomePage } from './pages/WelcomePage'
import { MailWorkspace } from './pages/MailWorkspace'
import { FontFixturesPage } from './pages/FontFixturesPage'
import { PasskeyPage } from './pages/PasskeyPage'
import { RecoveryPage } from './pages/RecoveryPage'
import { RegisterPasskeyPage } from './pages/RegisterPasskeyPage'
import { SignupPasskeyPage } from './pages/SignupPasskeyPage'
import { SessionsPage } from './pages/SessionsPage'
import { BG_PRESETS, useUiStore } from './lib/uiStore'
import { useSessionBootstrap } from './lib/useSession'
import { useSettingsSync } from './lib/useSettingsSync'
import { changeUiLanguage } from './i18n'

export function App() {
  const { i18n } = useTranslation()
  const theme = useUiStore((s) => s.theme)
  const locale = useUiStore((s) => s.locale)
  const dir = useUiStore((s) => s.dir)
  const background = useUiStore((s) => s.background)
  const fontSizePx = useUiStore((s) => s.fontSizePx)
  const listDensity = useUiStore((s) => s.listDensity)
  const a11yHighContrast = useUiStore((s) => s.a11yHighContrast)
  const a11yReduceMotion = useUiStore((s) => s.a11yReduceMotion)
  const a11yComfort = useUiStore((s) => s.a11yComfort)
  const { bootstrapped } = useSessionBootstrap()
  useSettingsSync()

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = dir
    const apply = (resolved: 'light' | 'dark' | 'system') => {
      if (resolved === 'system') {
        const dark = window.matchMedia('(prefers-color-scheme: dark)').matches
        document.documentElement.dataset.theme = dark ? 'dark' : 'light'
      } else {
        document.documentElement.dataset.theme = resolved
      }
    }
    apply(theme)
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => apply('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [locale, dir, theme])

  useEffect(() => {
    if (i18n.language !== locale) {
      void changeUiLanguage(locale)
    }
  }, [locale, i18n])

  useEffect(() => {
    const root = document.documentElement
    const applyBg = () => {
      const resolvedDark = root.dataset.theme === 'dark'
      const bg = BG_PRESETS[background]
      root.style.setProperty('--mail-user-bg', resolvedDark ? bg.dark : bg.light)
    }
    applyBg()
    root.style.setProperty('--mail-font-size', `${fontSizePx}px`)
    root.dataset.density = listDensity
    root.dataset.hc = a11yHighContrast ? '1' : '0'
    root.dataset.reduceMotion = a11yReduceMotion ? '1' : '0'
    root.dataset.comfort = a11yComfort ? '1' : '0'
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyBg()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [background, fontSizePx, listDensity, a11yHighContrast, a11yReduceMotion, a11yComfort, theme])

  if (!bootstrapped) {
    return (
      <div className="shell boot">
        <p className="muted">…</p>
      </div>
    )
  }

  return (
    <>
      <SettingsModal />
      <Routes>
        <Route
          path="/inbox"
          element={
            <RequireAuth>
              <MailWorkspace />
            </RequireAuth>
          }
        />
        <Route
          path="/inbox/:messageId"
          element={
            <RequireAuth>
              <MailWorkspace />
            </RequireAuth>
          }
        />
        <Route element={<ShellLayout />}>
          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/passkey" element={<PasskeyPage />} />
          <Route path="/signup" element={<SignupPasskeyPage />} />
          <Route path="/recovery" element={<RecoveryPage />} />
          <Route path="/security/passkey" element={<RegisterPasskeyPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route
            path="/accounts"
            element={
              <RequireAuth>
                <Navigate to="/inbox?sec=add" replace />
              </RequireAuth>
            }
          />
          <Route path="/dev/font-fixtures" element={<FontFixturesPage />} />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Route>
      </Routes>
    </>
  )
}
